import ExpandIcon from "@mui/icons-material/ExpandMoreRounded";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Chip, Divider, Stack, Typography } from "@mui/material";
import { parseISO } from "date-fns";
import { Fragment, useMemo, useState } from "react";
import EntriliaMarkIcon from "@entrilia/shared/icons/EntriliaMarkIcon";
import { numberComparerDescBy } from "@entrilia/shared/utilities/arrayHelper";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import { convertISODateTimeWithSeconds } from "@entrilia/shared/utilities/dateUtils";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type {
  WorkflowActivity,
  WorkflowActivityExecutionLogSeverity,
  WorkflowActivityExecutionLogSource,
} from "../../../../../api/types/workflowTypes";
import SparklesIcon from "@entrilia/shared/icons/SparklesIcon";

interface Props {
  activity: WorkflowActivity;
  defaultExpanded?: boolean;
}

const severityColor: Record<WorkflowActivityExecutionLogSeverity, "textPrimary" | "warning" | "error"> = {
  Info: "textPrimary",
  Warning: "warning",
  Error: "error",
};

const TimelineIcon = ({ source }: { source: WorkflowActivityExecutionLogSource }) => {
  const isAgentSource = source === "TaskAgent" || source === "ValidationAgent" || source === "Supervisor";
  const iconSx = { width: 16, height: 16 };

  return (
    <Avatar
      variant="circular"
      sx={(t) => ({
        width: 20,
        height: 20,
        bgcolor: makeTintedBackgroundFromColor(t, isAgentSource ? t.palette.primary.main : t.palette.text.primary),
      })}
    >
      {isAgentSource ? (
        <SparklesIcon color="primary" sx={iconSx} />
      ) : source === "User" ? (
        <PersonIcon color="secondary" sx={iconSx} />
      ) : (
        <EntriliaMarkIcon color="secondary" sx={iconSx} />
      )}
    </Avatar>
  );
};

const WorkflowActivityExecutionLogsSection = ({ activity, defaultExpanded }: Props) => {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);

  const sortedLogs = useMemo(
    () => [...(activity.executionLogs ?? [])].sort(numberComparerDescBy((log) => parseISO(log.timestamp).getTime())),
    [activity.executionLogs]
  );

  if (sortedLogs.length === 0) {
    return null;
  }

  return (
    <Accordion
      variant="outlined"
      disableGutters
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandIcon />}>
        <Typography variant="subtitle1">Execution Log</Typography>
      </AccordionSummary>
      <AccordionDetails sx={(t) => ({ px: 2, py: 1.5, borderTop: `1px solid ${t.palette.divider}` })}>
        <Timeline sx={{ p: 0, m: 0, maxWidth: 960, [`&& .${timelineItemClasses.root}::before`]: { flex: 0, p: 0 } }}>
          {sortedLogs.map((log, index) => {
            const prevLog = index > 0 ? sortedLogs[index - 1] : undefined;

            const showRetryDivider =
              prevLog?.attemptNumber !== undefined &&
              log.attemptNumber !== undefined &&
              prevLog.attemptNumber > 1 &&
              prevLog.attemptNumber !== log.attemptNumber;

            return (
              <Fragment key={log.id}>
                {showRetryDivider && (
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", py: 1.5 }}>
                    <Chip
                      label={`Revising with Validation feedback — attempt ${defined(prevLog).attemptNumber}`}
                      variant="outlined"
                      color="secondary"
                    />
                    <Divider sx={{ flex: 1, borderStyle: "dashed" }} />
                  </Stack>
                )}
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot sx={{ m: 0, p: 0, border: "none", boxShadow: "none", bgcolor: "transparent" }}>
                      <TimelineIcon source={log.source ?? "System"} />
                    </TimelineDot>
                    {index < sortedLogs.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ pt: 0, pb: index < sortedLogs.length - 1 ? 2 : 0 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
                      <Typography variant="subtitle2">
                        {log.sourceDisplayName || autoFormatCamelCase(log.source ?? "") || "System"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {convertISODateTimeWithSeconds(log.timestamp)}
                      </Typography>
                    </Stack>
                    <Typography color={severityColor[log.severity]} sx={{ pt: 0.5 }}>
                      {log.message}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Fragment>
            );
          })}
        </Timeline>
      </AccordionDetails>
    </Accordion>
  );
};

export default WorkflowActivityExecutionLogsSection;
